import { onlineManager } from '@tanstack/react-query';
import { createRoute, redirect, useParams } from '@tanstack/react-router';
import { Suspense, lazy } from 'react';
import { z } from 'zod';
import { i18n } from '~/lib/i18n';
import ErrorNotice from '~/modules/common/error-notice';
import Spinner from '~/modules/common/spinner';
import { toaster } from '~/modules/common/toaster';
import { workspaceQueryOptions } from '~/modules/workspaces/query';
import { baseEntityRoutes } from '~/nav-config';
import { queryClient } from '~/query/query-client';
import { AppRoute } from '~/routes/base';
import { noDirectAccess } from '~/utils/no-direct-access';

// Lazy-loaded components
const WorkspacePage = lazy(() => import('~/modules/workspaces/workspace-page'));
const Board = lazy(() => import('~/modules/tasks/board'));
const TasksTable = lazy(() => import('~/modules/tasks/table/table-wrapper'));
const Overview = lazy(() => import('~/modules/workspaces/workspace-overview'));

export const tasksSearchSchema = z.object({
  q: z.string().optional(),
  sort: z.enum(['projectId', 'status', 'createdBy', 'type', 'modifiedAt', 'createdAt']).default('createdAt').optional(),
  order: z.enum(['asc', 'desc']).default('asc').optional(),
  projectId: z.string().optional(),
  status: z.string().optional(),
  taskSheetId: z.string().optional(),
  userSheetId: z.string().optional(),
  sheetContext: z.string().optional(),
});

export const boardSearchSchema = z.object({
  project: z.string().optional(),
  q: z.string().optional(),
  taskSheetId: z.string().optional(),
  userSheetId: z.string().optional(),
  sheetContext: z.string().optional(),
});

export const labelsSearchSchema = z.object({
  q: z.string().optional(),
  sort: z.enum(['name', 'useCount', 'lastUsedAt']).default('name').optional(),
  order: z.enum(['asc', 'desc']).default('asc').optional(),
});

export const workspaceSearchSchema = z.object({
  ...boardSearchSchema.shape,
  ...tasksSearchSchema.shape,
});

export const WorkspaceRoute = createRoute({
  path: baseEntityRoutes.workspace,
  validateSearch: workspaceSearchSchema,
  staticData: { pageTitle: 'Workspace', isAuth: true },
  getParentRoute: () => AppRoute,
  beforeLoad: async ({ location: { pathname }, params: { idOrSlug, orgIdOrSlug } }) => {
    noDirectAccess(pathname, idOrSlug, '/board');

    const queryOptions = workspaceQueryOptions(idOrSlug, orgIdOrSlug);

    const options = { ...queryOptions, revalidateIfStale: true };
    const isOnline = onlineManager.isOnline();
    const workspace = isOnline ? await queryClient.ensureQueryData(options) : queryClient.getQueryData(queryOptions.queryKey);
    if (!workspace) {
      toaster(i18n.t('common:offline_cache_miss.text'), 'warning');
      throw redirect({ to: '/home', replace: true });
    }
    return { workspace };
  },
  loader: async ({ context: { workspace } }) => workspace,
  errorComponent: ({ error }) => <ErrorNotice level="app" error={error} />,
  component: () => {
    const { idOrSlug } = useParams({ from: WorkspaceRoute.id });
    return (
      // Pass identifier key to ensure a re-render when it changes
      <Suspense fallback={<Spinner className="mt-[45vh] h-10 w-10" />}>
        <WorkspacePage key={idOrSlug} />
      </Suspense>
    );
  },
});

export const WorkspaceBoardRoute = createRoute({
  path: '/board',
  staticData: { pageTitle: 'board', isAuth: true, floatingNavButtons: ['menu', 'create-task'] },
  validateSearch: boardSearchSchema,
  getParentRoute: () => WorkspaceRoute,
  component: () => (
    <Suspense>
      <Board />
    </Suspense>
  ),
});

export const WorkspaceTableRoute = createRoute({
  path: '/table',
  validateSearch: tasksSearchSchema,
  staticData: { pageTitle: 'table', isAuth: true },
  getParentRoute: () => WorkspaceRoute,
  component: () => (
    <Suspense>
      <TasksTable />
    </Suspense>
  ),
});

export const WorkspaceOverviewRoute = createRoute({
  path: '/overview',
  staticData: { pageTitle: 'overview', isAuth: true },
  getParentRoute: () => WorkspaceRoute,
  component: () => <Overview />,
});
